import Graph from './Graph';
import * as d3 from 'd3';
import { SimulationNodeDatum, style } from 'd3';
import avoidLinkForce from './avoidLinkForce';
import linkLengthForce from './linkLengthForce';
import groupForce from './groupForce';
import { NCNode } from '../../../../src/commonTypes';
import { GRAPH_ALPHA_DECAY, GRAPH_FORCE_PADDING, GRAPH_FORCE_STRENGTH, MIN_GRAPH_ALPHA } from '../constants';

export const initPhysics = (graph: Graph) => {
	if (graph.isPhysicsActive()) {
		console.log('[initPhysics called]');
		graph.nodes.forEach(node => {
            delete node.fx;
            delete node.fy;
        });
		const simulation = d3
			.forceSimulation(graph.nodes)
			.force(
				'collision',
				d3
					.forceCollide()
					.radius(
						(node) => getCollisionForce(graph,node)
					)
					.strength(GRAPH_FORCE_STRENGTH)
			)
			.force('linkCollision', avoidLinkForce(graph.links))
			.force('linkLength', linkLengthForce(graph.links))
			.force('group', groupForce(graph.nodeGroups))
			.on('tick', () => {
				// Check if any node is being dragged
				const isAnyNodeBeingDragged = graph.isDragging;
                
				// Only apply alpha decay if no node is being dragged
				if (!isAnyNodeBeingDragged) {
					 simulation.alphaDecay(GRAPH_ALPHA_DECAY);
				} else {
					 simulation.alphaDecay(0); // Prevent alpha from decaying during drag
				}
				graph.onTick();
				
			});
		simulation.alphaDecay(GRAPH_ALPHA_DECAY);
		simulation.alphaTarget(0);
		// simulation.stop();
		simulation.alpha(calculateAlpha(graph)).restart();
		// .velocityDecay(0.2);
		return simulation;
	}
	return undefined;
};

export const resetPhysicsMovement = (graph: Graph) => {
	if (graph.simulation) {
		// graph.nodes.forEach(node => {
		// 	delete node.fx;
        //     delete node.fy;
        // });
		graph.simulation.nodes(graph.nodes);
		const linkLength = graph.simulation.force('linkLength') as ReturnType<typeof linkLengthForce>;
		linkLength?.links(graph.links);
		const linkCollision = graph.simulation.force('linkCollision') as ReturnType<typeof avoidLinkForce>;
		linkCollision?.links(graph.links);
		const group = graph.simulation.force('group') as ReturnType<typeof groupForce>;
		group?.groups(graph.nodeGroups);

		graph.simulation.alpha(calculateAlpha(graph)).restart();
	}
};

export const calculateAlpha = (graph: Graph) => {
	
	if (!graph.currentDraggedNodeCluster) {
		// Get size of the graph
		const size = graph.nodes.length;
		return Math.max(0.2 - size * 0.002, 0.01);
	}
	const clusterSize = graph.currentDraggedNodeCluster?.length ?? 0;
	if (clusterSize > 30) {
		const alpha = Math.max(0.2 - clusterSize * 0.005, 0.01);
		return alpha;
	} else {
		return 0.1;
	}
}

export const ensurePhysicsAlphaIsHighEnough = (graph: Graph) => {
	if (graph.simulation) {
		if (graph.simulation.alpha() < MIN_GRAPH_ALPHA) {
			graph.simulation.alpha(MIN_GRAPH_ALPHA);
		}
	}
};

// export const updatePhysics = (graph: Graph) => {
// 	if (graph.isPhysicsActive() && graph.simulation) {
// 		console.log('[updatePhysics]');

// 		graph.simulation.nodes(graph.nodes);
// 		const linkCollision = graph.simulation.force('linkCollision') as ReturnType<
// 			typeof avoidLinkForce
// 		>;
// 		linkCollision?.links(graph.links);
// 		const linkLength = graph.simulation.force('linkLength') as ReturnType<typeof linkLengthForce>;
// 		linkLength?.links(graph.links);
// 		const group = graph.simulation.force('group') as ReturnType<typeof groupForce>;
// 		group?.groups(graph.nodeGroups);
// 		graph.simulation.alpha(1).restart();
// 	}
// };

export const updatePhysics = (graph: Graph) => {
	if (graph.isPhysicsActive() && graph.simulation) {
		console.log('[updatePhysics] nodes', graph.nodes.length);
		graph.simulation.nodes(graph.nodes);
		const linkCollision = graph.simulation.force('linkCollision') as ReturnType<
			typeof avoidLinkForce
		>;
		linkCollision?.links(graph.links);
		const linkLength = graph.simulation.force('linkLength') as ReturnType<typeof linkLengthForce>;
		linkLength?.links(graph.links);
		const group = graph.simulation.force('group') as ReturnType<typeof groupForce>;
		group?.groups(graph.nodeGroups);
		const collisionForce = graph.simulation.force('collision') as d3.ForceCollide<NCNode>;
		
		if (!collisionForce) {
			graph.simulation.force(
				'collision',
				d3
					.forceCollide()
					.radius(
						(node) =>
							getCollisionForce(graph, node)
					)
					.strength(GRAPH_FORCE_STRENGTH)
			);
		} else {
			collisionForce
				.radius(
					(node) =>
						getCollisionForce(graph, node)
				)
				.strength(GRAPH_FORCE_STRENGTH)
		}
		// console.log('[updatePhysics] alpha', graph.simulation.alpha());
		
		graph.simulation.alphaDecay(GRAPH_ALPHA_DECAY);
		graph.simulation.alphaTarget(0);
		graph.simulation.alpha(1).restart();
	}
};

const getCollisionForce = (graph: Graph, node: SimulationNodeDatum) => {
	const isPartOfGroup = graph.isNodeInGroup(node as NCNode);
	const nodeDensityFactor = Math.min(1, 500 / graph.nodes.length);

	const styleFactor = isPartOfGroup ? 2 : graph.nodes[node.index!]?.style === 'rect' ? 3 : 2;
	
	const padding = GRAPH_FORCE_PADDING;
	return graph.nodeRadius * nodeDensityFactor * styleFactor + padding;
}
